import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { Loader2, Search, PackageCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

function stepColor(step, completato) {
  if (completato) return "rgb(0,255,0)";
  if (step.value) return "#f5c518";
  return "#d1d5db";
}

export default function TracciaOrdine() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);

  const cerca = async () => {
    if (!validEmail(email)) return toast.error("Inserisci un'email valida");
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/ordini/track`, { params: { email }, timeout: 15000 });
      if (!data || data.length === 0) {
        toast.error("Nessun ordine trovato per questa email");
        setOrder(null);
      } else {
        setOrder(data[0]);
        window.scrollTo(0, 0);
      }
    } catch (e) {
      toast.error("Errore nel recupero dell'ordine. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="traccia-ordine-page" className="min-h-screen bg-background">
      <Seo
        title="Traccia il tuo Ordine | VETRO DESIGN ITALIA"
        description="Segui lo stato di avanzamento del tuo ordine di vetrate panoramiche VEPA."
        path="/traccia-ordine"
      />
      <Navbar />

      <div className="max-w-[1100px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-24">
        <div className="flex items-center justify-center gap-3 mb-10">
          <span className={`h-1 w-16 ${!order ? "bg-teal-600" : "bg-black/15"}`} />
          <span className={`h-1 w-16 ${order ? "bg-teal-600" : "bg-black/15"}`} />
        </div>

        {!order ? (
          <div data-testid="traccia-step1" className="max-w-xl mx-auto">
            <p className="text-xs uppercase tracking-[0.3em] text-teal-700 text-center">Passo 1 di 2</p>
            <h1 className="font-serif font-light text-3xl md:text-5xl text-center mt-3 text-[#26241f]">
              Traccia il tuo Ordine
            </h1>
            <p className="mt-6 text-center text-foreground/70 leading-relaxed">
              Inserisci l'email che hai comunicato in fase d'ordine per visualizzare lo stato di avanzamento dei lavori.
            </p>
            <div className="mt-10 space-y-5">
              <div>
                <label className="block text-sm text-foreground/70 mb-2">Email *</label>
                <input
                  data-testid="traccia-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && cerca()}
                  className="w-full bg-white border border-black/15 px-4 py-3 focus:outline-none focus:border-teal-600 transition-colors"
                  placeholder="email@esempio.it"
                />
              </div>
              <button
                data-testid="traccia-cerca"
                onClick={cerca}
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-3 bg-teal-600 text-white px-8 py-4 text-sm uppercase tracking-wide hover:bg-teal-700 transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" strokeWidth={1.6} />}
                Visualizza Ordine
              </button>
            </div>
          </div>
        ) : (
          <div data-testid="traccia-step2">
            <p className="text-xs uppercase tracking-[0.3em] text-teal-700 text-center">Passo 2 di 2</p>
            <h1 className="font-serif font-light text-3xl md:text-5xl text-center mt-3 text-[#26241f]">
              Traccia il tuo Ordine
            </h1>
            <div className="mt-6 text-center">
              <p className="text-foreground/70">
                Ordine <strong className="text-[#26241f]">{order.numero_ordine}</strong>
                {order.cliente_nome ? ` — ${order.cliente_nome}` : ""}
              </p>
              {order.completato && (
                <p data-testid="traccia-completato" className="mt-2 inline-flex items-center gap-2 text-[#26241f] font-medium">
                  <PackageCheck className="w-5 h-5" style={{ color: "rgb(0,255,0)" }} /> Ordine completato!
                </p>
              )}
            </div>

            <div className="mt-12 overflow-x-auto pb-4">
              <div className="flex items-start" style={{ minWidth: 980 }} data-testid="traccia-progress">
                {order.steps.map((s, i) => {
                  const color = stepColor(s, order.completato);
                  const nextColor = i < order.steps.length - 1 ? stepColor(order.steps[i + 1], order.completato) : color;
                  const on = order.completato || !!s.value;
                  return (
                    <div key={s.key} className="flex-1 flex flex-col items-center" data-testid={`traccia-step-${s.key}`}>
                      <div className="flex items-center w-full">
                        <div className="h-1.5 flex-1" style={{ background: i === 0 ? "transparent" : color }} />
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold border-2"
                          style={{
                            background: on ? color : "#fff",
                            borderColor: on ? color : "#d1d5db",
                            color: on ? "#26241f" : "#9ca3af",
                          }}
                        >
                          {i}
                        </div>
                        <div className="h-1.5 flex-1" style={{ background: i === order.steps.length - 1 ? "transparent" : nextColor }} />
                      </div>
                      <div className="mt-3 text-center px-1">
                        <div className="text-xs font-medium text-[#26241f] leading-tight">{s.label}</div>
                        {s.value_fmt && <div className="text-xs text-teal-700 mt-1">{s.value_fmt}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-12 text-center">
              <button
                data-testid="traccia-indietro"
                onClick={() => {
                  setOrder(null);
                  setEmail("");
                }}
                className="inline-flex items-center justify-center gap-2 border border-black/20 px-6 py-3 text-sm uppercase tracking-wide hover:bg-black/5 transition-colors"
              >
                Cerca un altro ordine
              </button>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
